import {
  Check,
  Download,
  FileImage,
  Grid3X3,
  Info,
  Printer,
  RefreshCcw,
  Ruler,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import ImageStage from './components/ImageStage'
import {
  GRID_PRESETS,
  calculateTilePlan,
  getCalibrationFactor,
  getPosterSizeMm,
  type Orientation,
} from './lib/layout'
import { generateCalibrationPdf, generatePrintPdf } from './lib/pdf'

type OutputKind = 'print' | 'calibration'

interface PdfOutput {
  url: string
  filename: string
  kind: OutputKind
  pageCount?: number
}

function clampNumber(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min
  return Math.min(max, Math.max(min, value))
}

export default function App() {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [imageName, setImageName] = useState('')
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [activePreset, setActivePreset] = useState('beads-mini-78')
  const [cols, setCols] = useState(78)
  const [rows, setRows] = useState(78)
  const [cellMm, setCellMm] = useState(2.6)
  const [orientation, setOrientation] = useState<Orientation>('portrait')
  // 78 × 2.6 mm = 202.8 mm；A4 短边 210 mm，因此 3.5 mm/边可以把整块 78 板放进一张 A4。
  // 对不支持窄边打印的设备，用户仍可手动把边距调大并接受自动分页。
  const [marginMm, setMarginMm] = useState(3.5)
  const [overlapMm, setOverlapMm] = useState(5)
  const [showGrid, setShowGrid] = useState(true)
  const [gridOpacity, setGridOpacity] = useState(0.28)
  const [zoom, setZoom] = useState(1)
  const [offsetXmm, setOffsetXmm] = useState(0)
  const [offsetYmm, setOffsetYmm] = useState(0)
  const [measured100Mm, setMeasured100Mm] = useState(100)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [renderDpi, setRenderDpi] = useState(220)
  const [exporting, setExporting] = useState(false)
  const [output, setOutput] = useState<PdfOutput | null>(null)
  const [error, setError] = useState<string | null>(null)

  const poster = useMemo(() => getPosterSizeMm(cols, rows, cellMm), [cols, rows, cellMm])
  const calibratedPoster = useMemo(() => {
    const factor = getCalibrationFactor(measured100Mm)
    return { widthMm: poster.widthMm * factor, heightMm: poster.heightMm * factor, factor }
  }, [poster, measured100Mm])

  const plan = useMemo(() => {
    try {
      return calculateTilePlan({
        posterWidthMm: calibratedPoster.widthMm,
        posterHeightMm: calibratedPoster.heightMm,
        orientation,
        marginMm,
        overlapMm,
      })
    } catch {
      return null
    }
  }, [calibratedPoster, orientation, marginMm, overlapMm])

  const choosePreset = (id: string) => {
    const preset = GRID_PRESETS.find((item) => item.id === id)
    if (!preset) return
    setActivePreset(id)
    setCols(preset.cols)
    setRows(preset.rows)
    setCellMm(preset.cellMm)
    setZoom(1)
    setOffsetXmm(0)
    setOffsetYmm(0)
  }

  const loadFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('请选择 PNG、JPG、WebP 等图片文件。')
      return
    }
    if (file.size > 40 * 1024 * 1024) {
      setError('图片超过 40 MB。为了避免手机浏览器内存不足，请先适当压缩。')
      return
    }

    if (imageSrc) URL.revokeObjectURL(imageSrc)
    const url = URL.createObjectURL(file)
    const probe = new Image()
    probe.onload = () => {
      setImageSrc(url)
      setImageName(file.name)
      setImageSize({ width: probe.naturalWidth, height: probe.naturalHeight })
      setZoom(1)
      setOffsetXmm(0)
      setOffsetYmm(0)
      setError(null)
    }
    probe.onerror = () => {
      URL.revokeObjectURL(url)
      setError('图片读取失败，请换一张图片重试。')
    }
    probe.src = url
  }

  const replaceOutput = (next: PdfOutput) => {
    if (output) URL.revokeObjectURL(output.url)
    setOutput(next)
  }

  const exportPrintPdf = async () => {
    if (!imageSrc) {
      setError('请先导入图片。')
      return
    }
    if (!plan) {
      setError('当前页面参数无法分页，请检查页边距与重叠设置。')
      return
    }

    setExporting(true)
    setError(null)
    try {
      const result = await generatePrintPdf({
        imageSrc,
        settings: {
          cols,
          rows,
          cellMm,
          orientation,
          marginMm,
          overlapMm,
          zoom,
          offsetXmm,
          offsetYmm,
          measured100Mm,
          showGrid,
          gridOpacity,
          renderDpi,
        },
      })
      const blobUrl = URL.createObjectURL(result.blob)
      const base = imageName.replace(/\.[^.]+$/, '') || 'truescale'
      replaceOutput({
        url: blobUrl,
        filename: `${base}-${cols}x${rows}-${cellMm}mm.pdf`,
        kind: 'print',
        pageCount: result.pageCount,
      })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'PDF 生成失败')
    } finally {
      setExporting(false)
    }
  }

  const exportCalibration = async () => {
    setExporting(true)
    setError(null)
    try {
      const blob = await generateCalibrationPdf()
      replaceOutput({
        url: URL.createObjectURL(blob),
        filename: 'truescale-calibration-100mm.pdf',
        kind: 'calibration',
      })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '校准页生成失败')
    } finally {
      setExporting(false)
    }
  }

  const resetImageTransform = () => {
    setZoom(1)
    setOffsetXmm(0)
    setOffsetYmm(0)
  }

  return (
    <main className="app-shell">
      <header className="app-header">
        <div className="brand-mark"><Ruler size={21} /></div>
        <div className="brand-copy">
          <h1>等尺打印</h1>
          <p>图片按真实毫米格距排版，生成不会“随便缩放”的 PDF</p>
        </div>
        <div className="privacy-note"><ShieldCheck size={15} /> 本地处理图片</div>
      </header>

      <div className="workspace">
        <div className="preview-column">
          <ImageStage
            imageSrc={imageSrc}
            imageWidthPx={imageSize.width}
            imageHeightPx={imageSize.height}
            cols={cols}
            rows={rows}
            cellMm={cellMm}
            zoom={zoom}
            offsetXmm={offsetXmm}
            offsetYmm={offsetYmm}
            showGrid={showGrid}
            gridOpacity={gridOpacity}
            onTransform={(next) => {
              setZoom(next.zoom)
              setOffsetXmm(next.offsetXmm)
              setOffsetYmm(next.offsetYmm)
            }}
            onPickImage={() => fileInputRef.current?.click()}
          />

          <section className="summary-strip" aria-label="输出摘要">
            <div><span>成品格区</span><strong>{poster.widthMm.toFixed(1)} × {poster.heightMm.toFixed(1)} mm</strong></div>
            <div><span>A4 页数</span><strong>{plan ? `${plan.columns} × ${plan.rows} = ${plan.tiles.length}` : '—'}</strong></div>
            <div><span>单格</span><strong>{cellMm.toFixed(2)} mm</strong></div>
          </section>
        </div>

        <aside className="control-panel">
          <section className="control-section">
            <div className="section-title"><FileImage size={17} /><span>1 · 图片</span></div>
            <input
              ref={fileInputRef}
              className="sr-only"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/avif"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) loadFile(file)
                event.currentTarget.value = ''
              }}
            />
            <button className="upload-button" type="button" onClick={() => fileInputRef.current?.click()}>
              <FileImage size={18} />
              <span>{imageSrc ? '更换图片' : '选择图片'}</span>
              {imageSrc && <small>{imageName}</small>}
            </button>
          </section>

          <section className="control-section">
            <div className="section-title"><Grid3X3 size={17} /><span>2 · 格网尺寸</span></div>
            <div className="preset-list">
              {GRID_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  className={`preset-button ${activePreset === preset.id ? 'active' : ''}`}
                  type="button"
                  onClick={() => choosePreset(preset.id)}
                >
                  <span className="preset-check">{activePreset === preset.id && <Check size={14} />}</span>
                  <span><strong>{preset.label}</strong><small>{preset.description}</small></span>
                </button>
              ))}
            </div>

            <div className="triple-fields">
              <label><span>横向格数</span><input type="number" min="1" max="400" value={cols} onChange={(e) => { setActivePreset('custom'); setCols(clampNumber(Number(e.target.value), 1, 400)) }} /></label>
              <label><span>纵向格数</span><input type="number" min="1" max="400" value={rows} onChange={(e) => { setActivePreset('custom'); setRows(clampNumber(Number(e.target.value), 1, 400)) }} /></label>
              <label><span>单格 mm</span><input type="number" min="0.5" max="100" step="0.1" value={cellMm} onChange={(e) => { setActivePreset('custom'); setCellMm(clampNumber(Number(e.target.value), 0.5, 100)) }} /></label>
            </div>
          </section>

          <section className="control-section">
            <div className="section-title"><Sparkles size={17} /><span>3 · 图片对齐</span></div>
            <label className="range-row">
              <span>缩放 <b>{zoom.toFixed(2)}×</b></span>
              <input type="range" min="0.5" max="4" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
            </label>
            <div className="inline-actions">
              <button className="secondary-button" type="button" onClick={resetImageTransform}><RefreshCcw size={15} /> 复位图片</button>
              <label className="toggle-row">
                <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} />
                <span>显示格线</span>
              </label>
            </div>
            {showGrid && (
              <label className="range-row compact">
                <span>格线深浅 <b>{Math.round(gridOpacity * 100)}%</b></span>
                <input type="range" min="0.08" max="0.7" step="0.01" value={gridOpacity} onChange={(e) => setGridOpacity(Number(e.target.value))} />
              </label>
            )}
          </section>

          <section className="control-section">
            <div className="section-title"><Printer size={17} /><span>4 · 纸张与分页</span></div>
            <div className="segmented">
              <button className={orientation === 'portrait' ? 'active' : ''} type="button" onClick={() => setOrientation('portrait')}>A4 竖向</button>
              <button className={orientation === 'landscape' ? 'active' : ''} type="button" onClick={() => setOrientation('landscape')}>A4 横向</button>
            </div>
            <div className="double-fields">
              <label><span>安全页边距 mm</span><input type="number" min="0" max="30" step="0.5" value={marginMm} onChange={(e) => setMarginMm(clampNumber(Number(e.target.value), 0, 30))} /></label>
              <label><span>拼接重叠 mm</span><input type="number" min="0" max="20" step="1" value={overlapMm} onChange={(e) => setOverlapMm(clampNumber(Number(e.target.value), 0, 20))} /></label>
            </div>
            {cols === 78 && rows === 78 && Math.abs(cellMm - 2.6) < 0.001 && marginMm <= 3.6 && (
              <p className="edge-note"><Info size={13} /> 78×78 的 2.6 mm 格区宽 202.8 mm；当前可单张 A4 输出。若你的打印机边缘打不全，把边距调大即可自动拆页。</p>
            )}
          </section>

          <section className="control-section advanced-section">
            <button className="advanced-toggle" type="button" onClick={() => setShowAdvanced((value) => !value)}>
              <SlidersHorizontal size={16} /> 高级 / 打印机校准 <span>{showAdvanced ? '收起' : '展开'}</span>
            </button>
            {showAdvanced && (
              <div className="advanced-body">
                <p><Info size={14} /> 先打印校准页，直尺量那条“100 mm”线。如果实际只有 99.3 mm，就在下面填 99.3，导出会自动反向补偿。</p>
                <div className="double-fields">
                  <label><span>100 mm 线实测</span><div className="input-suffix"><input type="number" min="80" max="120" step="0.01" value={measured100Mm} onChange={(e) => setMeasured100Mm(clampNumber(Number(e.target.value), 80, 120))} /><em>mm</em></div></label>
                  <label><span>页面图片清晰度</span><select value={renderDpi} onChange={(e) => setRenderDpi(Number(e.target.value))}><option value={160}>160 DPI · 更省内存</option><option value={220}>220 DPI · 推荐</option><option value={300}>300 DPI · 高质量</option></select></label>
                </div>
                <div className="calibration-status">
                  当前补偿：{(calibratedPoster.factor * 100).toFixed(3)}% · 补偿后单格 {(cellMm * calibratedPoster.factor).toFixed(4)} mm
                </div>
                <button className="secondary-button wide" type="button" onClick={exportCalibration}><Ruler size={15} /> 生成 100 mm 校准页</button>
              </div>
            )}
          </section>

          {error && <div className="error-banner" role="alert">{error}</div>}

          <div className="export-zone">
            <div className="export-copy">
              <span>预计输出</span>
              <strong>{plan ? `${plan.tiles.length} 页 A4 · ${poster.widthMm.toFixed(1)} × ${poster.heightMm.toFixed(1)} mm` : '检查分页参数'}</strong>
            </div>
            <button className="primary-button" type="button" disabled={!imageSrc || exporting || !plan} onClick={exportPrintPdf}>
              <Printer size={18} /> {exporting ? '正在生成…' : '生成打印 PDF'}
            </button>

            {output && (
              <a className="download-button" href={output.url} download={output.filename} target="_blank" rel="noreferrer">
                <Download size={17} />
                {output.kind === 'print' ? `打开 / 下载 PDF${output.pageCount ? `（${output.pageCount} 页）` : ''}` : '打开 / 下载校准页'}
              </a>
            )}
            <p className="print-warning"><Info size={14} /> 最后一步仍要在打印对话框选“实际大小 / 100%”，关闭“适合页面 / 缩放”。</p>
          </div>
        </aside>
      </div>
    </main>
  )
}
